import React from "react";
import { X } from "lucide-react";
import { useDismissedHint } from "./useDismissedHint";

export interface HintBannerProps {
  /**
   * What the hint is remembered by once it has been closed. One per hint,
   * stable across releases: changing it puts a closed hint back up.
   */
  storageKey: string;
  /** The one line that says what the screen under it is for. */
  title: string;
  /** The sentence under it, for the reader who wants the how. */
  description: string;
  className?: string;
}

/**
 * The card a screen carries the first few times it is opened, saying what the
 * thing under it is for — and the cross that takes it away for good.
 *
 * It is a hint, not a warning: the brand's own soft ground rather than the
 * `SetupBanner`'s warm one, because nothing is outstanding and nothing is
 * wrong. And unlike that strip, which the saved record decides, this one is
 * the reader's to close — and stays closed in this browser once it is, see
 * `useDismissedHint`.
 */
export const HintBanner: React.FC<HintBannerProps> = ({
  storageKey,
  title,
  description,
  className = "",
}) => {
  const [dismissed, dismiss] = useDismissedHint(storageKey);

  if (dismissed) return null;

  return (
    <section
      aria-label={title}
      className={`relative flex-shrink-0 rounded-md border border-brand/40 bg-brand-soft px-6 py-5 pr-14 3xl:px-7 3xl:py-6 ${className}`}
    >
      <h2 className="text-[17px] 3xl:text-[19px] font-medium leading-snug text-ink">
        {title}
      </h2>
      <p className="mt-1.5 text-[14px] 3xl:text-[15px] leading-[1.6] text-ink-muted">
        {description}
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded text-ink-muted transition-colors hover:bg-brand/10 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </section>
  );
};

export default HintBanner;
